import { useId, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { alterarVertical, type AlteracaoDaVertical, type CampanhaCompleta, type VerticalCompleta } from '../../api/cptu';
import {
  EDICAO_VAZIA, aplicarEdicao, correlacaoCruzada,
  type CanalDaEdicao, type CorrelacaoCruzada, type Edicao, type LeituraRemovida, type Leituras, type Premissas,
} from '../../cptu/motor';
import { colunasQueSaemDaHomologacao, numeroBr } from '../../cptu/visual';
import { faixaProfundidade, numero, paraCampo, paraNumero, plural } from '../../formato';
import { useSessao } from '../../sessao';
import { AcaoBloqueada, Area, Aviso, CampoNumerico, Dialogo, ErroDaApi } from '../../ui';
import { motivoDaCampanhaParada, PERMISSAO_DE_EDITAR_CPTU } from './CampanhaMoldura';
import { escolhasGravadas, interpretar, useDissipacoesNaTela } from './DissipacaoInterpretada';
import { FormularioDaJanela } from './FormularioDaJanela';
import { focarOPrimeiro, premissasParaAVertical, useFocoDeVolta, type Rascunho } from './interpretacao';

/**
 * A edição da vertical — onda 313, Tarefa 313.6 (spec 07 §5.4 e §6.1, item 4; R2, R7, R9, R10; RN-253).
 *
 * O que o engenheiro corrige SEM tocar no bruto: as leituras do equipamento (`cptu_leitura`) ficam como vieram, e a
 * edição é uma escolha da vertical (`cptu_vertical.edicao`), auditada, que o motor aplica antes das contas, na ordem
 * cortes → picos → deslocamento → filtros (`edicao.ts`):
 *
 *  · cortes (de, até, motivo) — e «Cortar o pré-furo» quando o cabeçalho tem o pré-furo;
 *  · picos (a janela ímpar, o limite em desvios da mediana e os canais), com as leituras que saem, uma a uma;
 *  · filtros por canal (média móvel própria de qc, fs e u₂), que SE SOMAM à média móvel das premissas — a tela diz;
 *  · deslocamento de fs e u₂ (cm), com a correlação cruzada qc × fs sugerindo a defasagem (o gráfico mora embaixo das
 *    trilhas: `CorrelacaoCruzadaGrafico`) — calculada sobre as leituras com os cortes e os picos do rascunho, sem
 *    deslocamento nem filtro, de −50 a +50 leituras;
 *  · u₀ não hidrostático (pontos z × u₀), que tira grandezas da homologação (a conta única do visual,
 *    `colunasQueSaemDaHomologacao`) — é aqui, e só aqui, que ele muda (o «Simular» e a aba Premissas o mostram para
 *    ler); voltar ao modo gravado volta os pontos gravados (rodada final da 313); e o NA sugerido pelas dissipações da
 *    vertical, como sugestão.
 *
 * Tudo vai para o rascunho da vertical (o mesmo do «Simular»): o antes é o gravado (cinza tracejado nas trilhas), o
 * depois é o rascunho, recalculado 150 ms depois da última mudança. «Gravar a edição» pede o motivo e manda, pelo
 * `PATCH /cptu/verticais/:id`, a edição INTEIRA a partir de `structuredClone(EDICAO_VAZIA)` (a vazia é congelada
 * fundo; a edição sem ajuste nenhum vai como `null`), as premissas INTEIRAS a partir do gravado quando o u₀ mudou
 * (R10: o PATCH troca o objeto todo) e o NA pelo cabeçalho quando ele mudou. O resto do rascunho (as premissas do
 * «Simular») fica nele. «Sair sem gravar a edição» tira do rascunho o que é desta vista (a edição, o u₀ e o NA).
 *
 * Sem `cptu.editar`, ou com a campanha fora de rascunho/devolvida, a edição fica para ler e «Gravar a edição» à
 * vista, bloqueado com o motivo (P-UI-24). No celular é só leitura, sem ação nenhuma (R8).
 */

/** De −50 a +50 leituras: a janela da correlação cruzada qc × fs (spec §5.4). */
export const MAXIMO_DA_CORRELACAO = 50;

const finitoOu = (v: unknown, padrao: number): number => (typeof v === 'number' && Number.isFinite(v) ? v : padrao);

/**
 * A edição inteira, na forma da `EDICAO_VAZIA` — a partir de uma CÓPIA dela (`structuredClone`: a vazia é congelada
 * fundo): o que falta vale «sem ajuste».
 */
export function edicaoInteira(e: Edicao | null | undefined): Edicao {
  const nova = structuredClone(EDICAO_VAZIA) as Edicao;
  if (!e) return nova;
  if (Array.isArray(e.cortes)) {
    nova.cortes = e.cortes.filter(Boolean).map((k) => ({ de: k.de, ate: k.ate, motivo: k.motivo }));
  }
  nova.picos = e.picos ? { janela: e.picos.janela, limite: e.picos.limite, canais: [...(e.picos.canais ?? [])] } : null;
  nova.deslocamento = { fs: finitoOu(e.deslocamento?.fs, 0), u2: finitoOu(e.deslocamento?.u2, 0) };
  nova.filtros = { qc: finitoOu(e.filtros?.qc, 1), fs: finitoOu(e.filtros?.fs, 1), u2: finitoOu(e.filtros?.u2, 1) };
  return nova;
}

/** As duas edições fazem o mesmo (null = sem ajuste). */
export const mesmaEdicao = (a: Edicao | null | undefined, b: Edicao | null | undefined): boolean =>
  JSON.stringify(edicaoInteira(a)) === JSON.stringify(edicaoInteira(b));

/** «−15 cm», «+5 cm», «0 cm» — o deslocamento com o sinal tipográfico. */
export function textoEmCm(v: number): string {
  if (!Number.isFinite(v)) return '—';
  const arredondado = Math.round(v * 100) / 100;
  const corpo = `${numero(Math.abs(arredondado), 2, true)} cm`;
  return arredondado < 0 ? `−${corpo}` : arredondado > 0 ? `+${corpo}` : corpo;
}

/** «1 leitura removida», «1.226 leituras removidas» — o `plural()` da casa, com o milhar. */
const contaComMilhar = (n: number, um: string, muitos: string): string =>
  plural(n, um, muitos).replace(/^\d+/, numeroBr(n, 0));

/** O passo das leituras em cm: a mediana das diferenças de profundidade (1 cm no Pagani e no .cpt). */
function passoDasLeituras(z: ArrayLike<number>): number {
  const difs: number[] = [];
  for (let i = 1; i < z.length; i++) {
    const d = z[i] - z[i - 1];
    if (Number.isFinite(d) && d > 0) difs.push(d);
  }
  if (difs.length === 0) return NaN;
  difs.sort((a, b) => a - b);
  const meio = Math.floor(difs.length / 2);
  const mediana = difs.length % 2 ? difs[meio] : (difs[meio - 1] + difs[meio]) / 2;
  return Math.round(mediana * 100 * 100) / 100;
}

/** O que a edição tira das leituras (com o índice do BRUTO) e a correlação cruzada que sugere o deslocamento do fs. */
export interface CorrelacaoDaEdicao {
  removidas: LeituraRemovida[];
  cc: CorrelacaoCruzada;
  /** O passo das leituras, em cm. */
  passoCm: number;
  /** O deslocamento do fs sugerido, em cm: −(a defasagem) × o passo (o fs atrasado sobe); NaN sem sugestão. */
  sugestaoCm: number;
}

/**
 * Os cortes e os picos da edição sobre o bruto — como o motor faz: a leitura sem profundidade sai antes —, e a
 * correlação cruzada qc × fs nas leituras que sobram, sem deslocamento nem filtro, de −50 a +50 leituras.
 */
export function correlacaoDaEdicao(bruto: Leituras, e: Edicao): CorrelacaoDaEdicao {
  const n = bruto.profundidade.length;
  const validas: number[] = [];
  for (let i = 0; i < n; i++) if (Number.isFinite(bruto.profundidade[i])) validas.push(i);
  const escolher = (s: Float64Array) => Float64Array.from(validas, (i) => s[i]);
  const entrada: Leituras = validas.length === n ? bruto : {
    profundidade: escolher(bruto.profundidade), qc: escolher(bruto.qc), fs: escolher(bruto.fs), u2: escolher(bruto.u2),
  };
  const so = edicaoInteira({ ...edicaoInteira(null), cortes: e.cortes, picos: e.picos });
  const aplicada = aplicarEdicao(entrada, so);
  const removidas = aplicada.removidas.map((x) => ({ ...x, ordem: validas[x.ordem] }));
  const cc = correlacaoCruzada(aplicada.leituras.qc, aplicada.leituras.fs, MAXIMO_DA_CORRELACAO);
  const passoCm = passoDasLeituras(aplicada.leituras.profundidade);
  const bruta = -cc.defasagem * passoCm;
  const sugestaoCm = Number.isFinite(bruta) ? Math.round(bruta * 100) / 100 + 0 : NaN;
  return { removidas, cc, passoCm, sugestaoCm };
}

/** «226 leituras removidas por corte (pré-furo)», «9 leituras removidas por pico em qc» — as frases do motor. */
export function frasesDasRemovidas(removidas: readonly LeituraRemovida[], pico: boolean): string[] {
  const porMotivo = new Map<string, number>();
  for (const x of removidas) {
    if (x.motivo.startsWith('pico em ') !== pico) continue;
    porMotivo.set(x.motivo, (porMotivo.get(x.motivo) ?? 0) + 1);
  }
  return [...porMotivo].map(([motivo, q]) => {
    const quantas = contaComMilhar(q, 'leitura removida', 'leituras removidas');
    if (pico) return `${quantas} por ${motivo}`;
    return motivo === 'corte' ? `${quantas} por corte` : `${quantas} por corte (${motivo})`;
  });
}

/** «0,00 – 2,26 m · pré-furo» — a faixa como o documento a escreve (P-FMT-05). */
const textoDoCorte = (k: Edicao['cortes'][number]) => `${faixaProfundidade(k.de, k.ate)} m · ${k.motivo || 'corte'}`;
const NOME_DO_CANAL: Readonly<Record<CanalDaEdicao, string>> = { qc: 'qc', fs: 'fs', u2: 'u₂' };
const listaDeCanais = (canais: readonly CanalDaEdicao[]) => canais.map((x) => NOME_DO_CANAL[x]).join(', ');

/** As partes da edição escritas, para ler e para a janela de gravar. */
function partesDaEdicao(e: Edicao): { cortes: string; picos: string; filtros: string; deslocamento: string } {
  return {
    cortes: e.cortes.length === 0 ? 'nenhum' : e.cortes.map(textoDoCorte).join('; '),
    picos: e.picos
      ? `janela ${numero(e.picos.janela, 0)} · limite ${numero(e.picos.limite, 2, true)} · em ${listaDeCanais(e.picos.canais)}`
      : 'não remove picos',
    filtros: `qc ${numero(e.filtros.qc, 0)} · fs ${numero(e.filtros.fs, 0)} · u₂ ${numero(e.filtros.u2, 0)}`,
    deslocamento: `fs ${textoEmCm(e.deslocamento.fs)} · u₂ ${textoEmCm(e.deslocamento.u2)}`,
  };
}

/** O u₀ escrito: «hidrostático» ou «por pontos: 3 pontos (0,00 m: 0,00 kPa; …)». */
function textoDoU0(modo: Premissas['u0Modo'], pontos: Premissas['u0Pontos']): string {
  if (modo !== 'pontos') return 'hidrostático';
  const lista = pontos.map((q) => `${numeroBr(q.z, 2)} m: ${numeroBr(q.u0, 2)} kPa`).join('; ');
  return `por pontos: ${plural(pontos.length, 'ponto', 'pontos')}${lista ? ` (${lista})` : ''}`;
}

/** As grandezas homologadas que o u₀ por pontos tira do PDF e do Portal — a conta única (rodada da 313.5). */
const COM_U0_POR_PONTOS = colunasQueSaemDaHomologacao({ u0Modo: 'pontos' });

/** O que o rascunho tem desta vista: a edição, o u₀ e o NA. */
function partesDestaVista(r: Rascunho | null): { edicao: boolean; u0: boolean; na: boolean } {
  return {
    edicao: r?.edicao !== undefined,
    u0: r?.premissas?.u0Modo !== undefined || r?.premissas?.u0Pontos !== undefined,
    na: r?.nivelAgua !== undefined,
  };
}

/** O rascunho sem o que é desta vista (a edição, o u₀, o NA): o que fica depois de gravar ou sair. */
export function rascunhoSemAEdicao(r: Rascunho | null): Rascunho | null {
  if (!r) return null;
  const resto: Rascunho = { ...r };
  delete resto.edicao;
  delete resto.nivelAgua;
  if (resto.premissas) {
    const p = { ...resto.premissas };
    delete p.u0Modo;
    delete p.u0Pontos;
    if (Object.keys(p).length > 0) resto.premissas = p; else delete resto.premissas;
  }
  return Object.keys(resto).length > 0 ? resto : null;
}

/**
 * Os pontos que reproduzem o u₀ hidrostático: (NA, 0) e (profundidade final, γw·(z − NA)), z ao centímetro e u₀ ao
 * milésimo de kPa (o que o campo escreve; z crescente, como a API exige). O motor interpola em linha reta entre eles e
 * mantém o do mais perto fora deles — acima do NA, 0 —, que é o hidrostático. Sem profundidade final abaixo do NA, só o
 * do NA.
 */
function pontosDoHidrostatico(na: number, profundidadeFinal: number | null, gamaW: number): Premissas['u0Pontos'] {
  const zNa = Math.round(na * 100) / 100;
  const g = Number.isFinite(gamaW) && gamaW > 0 ? gamaW : 9.81;
  if (profundidadeFinal === null || !Number.isFinite(profundidadeFinal) || profundidadeFinal <= zNa + 0.005) {
    return [{ z: zNa, u0: 0 }];
  }
  const zFim = Math.round(profundidadeFinal * 100) / 100;
  return [{ z: zNa, u0: 0 }, { z: zFim, u0: Math.round(g * (zFim - zNa) * 1000) / 1000 }];
}

/** Um número de um campo: o texto digitado, lido; undefined quando não se lê. */
const lerNumero = (t: string): number | undefined => {
  const n = paraNumero(t);
  return n !== undefined && Number.isFinite(n) ? n : undefined;
};

export function EdicaoDaVertical({
  c, v, gravadas, rascunho, correlacao, estreito, bloqueio, aoMudar, aoGravado,
}: {
  c: CampanhaCompleta;
  v: VerticalCompleta;
  /** As premissas efetivas do gravado (o u₀ de agora). */
  gravadas: Premissas;
  rascunho: Rascunho | null;
  /** Os cortes e os picos da edição que vale (a do rascunho, ou a gravada) e a correlação cruzada. */
  correlacao: CorrelacaoDaEdicao | null;
  estreito: boolean;
  bloqueio: 'permissao' | 'parada' | null;
  aoMudar(r: Rascunho | null): void;
  /** Gravado: `resto` é o que fica no rascunho (as premissas do «Simular»). */
  aoGravado(resto: Rascunho | null): Promise<void>;
}) {
  const { eu } = useSessao();
  const [textos, setTextos] = useState<Record<string, string>>({});
  const gravada = useMemo(() => edicaoInteira(v.edicao), [v.edicao]);
  const valendo: Edicao = rascunho?.edicao ?? gravada;
  const [picosLigado, setPicosLigado] = useState(valendo.picos !== null);
  const [canais, setCanais] = useState<CanalDaEdicao[]>(valendo.picos?.canais ?? []);
  const [janela, setJanela] = useState(false);
  const idDoGravar = useId();
  const soLer = estreito || !!bloqueio;
  // A janela devolve o foco a quem a abriu (rodada do agent-browser da 313).
  useFocoDeVolta(janela, 'section.cptu-edicao .cptu-edicao-acoes > button');

  const u0Modo = rascunho?.premissas?.u0Modo ?? gravadas.u0Modo;
  const u0Pontos = rascunho?.premissas?.u0Pontos ?? gravadas.u0Pontos;
  const naValendo = rascunho?.nivelAgua !== undefined ? rascunho.nivelAgua : v.nivel_agua;
  const partes = partesDestaVista(rascunho);
  const sujo = partes.edicao || partes.u0 || partes.na;

  /** O rascunho novo (vazio = nenhum). */
  const emitir = (prox: Rascunho) => {
    if (prox.premissas && Object.keys(prox.premissas).length === 0) delete prox.premissas;
    aoMudar(Object.keys(prox).length > 0 ? prox : null);
  };
  /** Uma edição nova no rascunho; igual à gravada, sai dele. */
  const mudarEdicao = (proxima: Edicao) => {
    const prox: Rascunho = { ...(rascunho ?? {}) };
    if (mesmaEdicao(proxima, gravada)) delete prox.edicao; else prox.edicao = edicaoInteira(proxima);
    emitir(prox);
  };
  /** O u₀ (o modo e os pontos) no rascunho; igual ao gravado, sai dele. */
  const mudarU0 = (modo: Premissas['u0Modo'], pontos: Premissas['u0Pontos']) => {
    const premissas: Partial<Premissas> = { ...(rascunho?.premissas ?? {}) };
    if (modo === gravadas.u0Modo) delete premissas.u0Modo; else premissas.u0Modo = modo;
    if (JSON.stringify(pontos) === JSON.stringify(gravadas.u0Pontos)) delete premissas.u0Pontos;
    else premissas.u0Pontos = pontos.map((q) => ({ z: q.z, u0: q.u0 }));
    emitir({ ...(rascunho ?? {}), premissas });
  };
  const usarNa = (na: number) => {
    const novo = Math.round(na * 100) / 100;
    const prox: Rascunho = { ...(rascunho ?? {}) };
    if (novo === v.nivel_agua) delete prox.nivelAgua; else prox.nivelAgua = novo;
    emitir(prox);
  };
  const texto = (chave: string, padrao: string) => textos[chave] ?? padrao;
  const escrever = (chave: string, t: string) => setTextos((x) => ({ ...x, [chave]: t }));

  /* ── O que não deixa gravar (cada problema também aparece no lugar dele) ── */
  const problemasDoCorte = (i: number, k: Edicao['cortes'][number]): string[] => {
    const p: string[] = [];
    for (const lado of ['de', 'ate'] as const) {
      const t = textos[`corte.${i}.${lado}`];
      if (t !== undefined && lerNumero(t) === undefined) p.push(`«${lado === 'de' ? 'de' : 'até'}» não se lê: use vírgula para as casas`);
    }
    if (!(k.de < k.ate)) p.push('o corte vai de uma profundidade a outra maior («de» menor que «até»)');
    if (!k.motivo.trim()) p.push('diga o motivo do corte (vai para a lista das removidas)');
    return p;
  };
  const janelaTxt = texto('picos.janela', valendo.picos ? String(valendo.picos.janela) : '');
  const limiteTxt = texto('picos.limite', valendo.picos ? paraCampo(valendo.picos.limite, 4) : '');
  const problemasDosPicos = (jt: string, lt: string, cs: readonly CanalDaEdicao[]): string[] => {
    const p: string[] = [];
    const j = lerNumero(jt);
    const l = lerNumero(lt);
    if (j === undefined || !Number.isInteger(j) || j < 3 || j % 2 === 0) p.push('a janela é um número inteiro ímpar de leituras, 3 ou mais');
    if (l === undefined || !(l > 0)) p.push('o limite (desvios da mediana) é um número maior que zero');
    if (cs.length === 0) p.push('escolha ao menos um canal');
    return p;
  };
  const picosDe = (jt: string, lt: string, cs: readonly CanalDaEdicao[]): Edicao['picos'] =>
    (problemasDosPicos(jt, lt, cs).length > 0 ? null
      : { janela: lerNumero(jt) as number, limite: lerNumero(lt) as number, canais: (['qc', 'fs', 'u2'] as CanalDaEdicao[]).filter((x) => cs.includes(x)) });
  const problemasDoNumero = (chave: string, inteiro: boolean, oque: string): string[] => {
    const t = textos[chave];
    if (t === undefined) return [];
    const n = lerNumero(t);
    if (n === undefined) return [`${oque} não se lê: use vírgula para as casas`];
    if (inteiro && !(Number.isInteger(n) && n >= 1)) return [`${oque} é um número inteiro de leituras, 1 ou mais (1 = sem filtro)`];
    return [];
  };
  const problemasDoU0 = (): string[] => {
    if (u0Modo !== 'pontos') return [];
    const p: string[] = [];
    if (u0Pontos.length === 0) p.push('com o u₀ por pontos, informe pelo menos um ponto (z, u₀)');
    for (let i = 1; i < u0Pontos.length; i++) {
      if (!(u0Pontos[i].z > u0Pontos[i - 1].z)) { p.push('os pontos de u₀ vão em profundidade crescente'); break; }
    }
    for (const [chave, t] of Object.entries(textos)) {
      if (chave.startsWith('u0.') && lerNumero(t) === undefined) { p.push('um ponto de u₀ não se lê: use vírgula para as casas'); break; }
    }
    return p;
  };
  const problemas = [
    ...valendo.cortes.flatMap((k, i) => problemasDoCorte(i, k).map((x) => `Corte ${i + 1}: ${x}`)),
    ...(picosLigado ? problemasDosPicos(janelaTxt, limiteTxt, canais).map((x) => `Picos: ${x}`) : []),
    ...(['qc', 'fs', 'u2'] as const).flatMap((k) => problemasDoNumero(`filtro.${k}`, true, `O filtro de ${NOME_DO_CANAL[k]}`)),
    ...(['fs', 'u2'] as const).flatMap((k) => problemasDoNumero(`desloc.${k}`, false, `O deslocamento do ${NOME_DO_CANAL[k]}`)),
    ...problemasDoU0(),
  ];
  const motivoDoGravar = !sujo ? 'Nada mudou na edição ainda.'
    : problemas.length > 0 ? `Confira antes de gravar: ${problemas[0]}${problemas.length > 1 ? ` (e mais ${problemas.length - 1})` : ''}.`
      : null;

  const removidasPorCorte = correlacao ? frasesDasRemovidas(correlacao.removidas, false) : [];
  const removidasPorPico = correlacao ? frasesDasRemovidas(correlacao.removidas, true) : [];
  const picosRemovidos = correlacao ? correlacao.removidas.filter((x) => x.motivo.startsWith('pico em ')) : [];
  const preFuro = v.pre_furo_m !== null && v.pre_furo_m !== undefined && v.pre_furo_m > 0 ? v.pre_furo_m : null;
  const temCorteDoPreFuro = preFuro !== null && valendo.cortes.some((k) => k.de <= 0.005 && k.ate >= preFuro - 0.005);
  const mediaMovel = rascunho?.premissas?.mediaMovel ?? gravadas.mediaMovel;
  const outrasPremissas = Object.keys(rascunhoSemAEdicao(rascunho)?.premissas ?? {});

  const acaoBloqueada = (t: string) => (bloqueio === 'permissao'
    ? <AcaoBloqueada key={t} texto={t} oQueFaz="não grava a edição da vertical CPTu"
      permissao={PERMISSAO_DE_EDITAR_CPTU} persona={eu?.atuandoPor ? eu.nome : null} />
    : <AcaoBloqueada key={t} texto={t} motivo={motivoDaCampanhaParada(c.campanha.situacao)} />);

  if (soLer) {
    const lida = partesDaEdicao(valendo);
    return (
      <section className="cptu-edicao" aria-label="Edição da vertical">
        <header className="cptu-painel-cabeca">
          <h3>Edição</h3>
          <p className="cptu-nota">
            {v.edicao ? 'A edição gravada: aplicada antes das contas; as leituras do equipamento não mudam.'
              : 'Sem edição gravada: as contas usam as leituras como o equipamento as gravou.'}
          </p>
        </header>
        <dl className="cptu-resumo-edicao">
          <dt>Cortes</dt>
          <dd>{lida.cortes}{removidasPorCorte.length > 0 ? ` — ${removidasPorCorte.join('; ')}` : ''}</dd>
          <dt>Picos</dt>
          <dd>{lida.picos}{removidasPorPico.length > 0 ? ` — ${removidasPorPico.join('; ')}` : ''}</dd>
          <dt>Filtros por canal</dt>
          <dd>{lida.filtros} (1 = sem filtro; soma-se à média móvel de {plural(mediaMovel, 'leitura', 'leituras')} das premissas)</dd>
          <dt>Deslocamento</dt>
          <dd>{lida.deslocamento}</dd>
          <dt>u₀</dt>
          <dd>
            {textoDoU0(u0Modo, u0Pontos)}
            {u0Modo === 'pontos' && <> <span className="cptu-marca-texto">▲</span> fora das condições homologadas: {grandezasDoU0()}</>}
          </dd>
          <dt>NA</dt>
          <dd>{naValendo === null || naValendo === undefined ? 'não informado' : `${numeroBr(naValendo, 2)} m`}</dd>
        </dl>
        {bloqueio && !estreito && <div className="cptu-edicao-acoes">{acaoBloqueada('Gravar a edição')}</div>}
      </section>
    );
  }

  /* ── Os campos ── */
  const trocarCorte = (i: number, mudanca: Partial<Edicao['cortes'][number]>) =>
    mudarEdicao({ ...valendo, cortes: valendo.cortes.map((k, j) => (j === i ? { ...k, ...mudanca } : k)) });
  const tirarCorte = (i: number) => {
    setTextos((x) => Object.fromEntries(Object.entries(x).filter(([k]) => !k.startsWith('corte.'))));
    mudarEdicao({ ...valendo, cortes: valendo.cortes.filter((_, j) => j !== i) });
  };
  const campoDoCorte = (i: number, lado: 'de' | 'ate', k: Edicao['cortes'][number]) => {
    const chave = `corte.${i}.${lado}`;
    const t = texto(chave, paraCampo(k[lado], 2));
    return (
      <CampoNumerico rotulo={`Corte ${i + 1}: ${lado === 'de' ? 'de' : 'até'} (m)`} value={t}
        erro={lerNumero(t) === undefined ? 'Não se lê: use vírgula para as casas.' : undefined}
        onChange={(e) => {
          escrever(chave, e.target.value);
          const n = lerNumero(e.target.value);
          if (n !== undefined) trocarCorte(i, lado === 'de' ? { de: n } : { ate: n });
        }} />
    );
  };
  const mudarPicos = (ligado: boolean, jt: string, lt: string, cs: CanalDaEdicao[]) =>
    mudarEdicao({ ...valendo, picos: ligado ? picosDe(jt, lt, cs) : null });
  const campoInteiroOuNumero = (chave: string, rotulo: string, valor: number, inteiro: boolean,
    aplicar: (n: number) => void) => {
    const t = texto(chave, paraCampo(valor, 2));
    const n = lerNumero(t);
    const erro = n === undefined ? 'Não se lê: use vírgula para as casas.'
      : inteiro && !(Number.isInteger(n) && n >= 1) ? 'Um número inteiro de leituras, 1 ou mais.' : undefined;
    return (
      <CampoNumerico rotulo={rotulo} value={t} erro={erro} inteiro={inteiro}
        onChange={(e) => {
          escrever(chave, e.target.value);
          const lido = lerNumero(e.target.value);
          if (lido !== undefined && (!inteiro || (Number.isInteger(lido) && lido >= 1))) aplicar(lido);
        }} />
    );
  };
  const trocarPonto = (i: number, campo: 'z' | 'u0', t: string) => {
    escrever(`u0.${i}.${campo}`, t);
    const n = lerNumero(t);
    if (n === undefined) return;
    mudarU0('pontos', u0Pontos.map((q, j) => (j === i ? { ...q, [campo]: n } : q)));
  };
  const sugestaoUsavel = correlacao && Number.isFinite(correlacao.sugestaoCm) ? correlacao : null;

  return (
    <section className="cptu-edicao" aria-label="Edição da vertical">
      <header className="cptu-painel-cabeca">
        <h3>Edição</h3>
        <p className="cptu-nota">
          As leituras do equipamento não mudam: a edição se aplica antes das contas. O gravado aparece em cinza tracejado
          (o antes); o que você muda aqui recalcula na hora (o depois).
        </p>
      </header>

      <fieldset className="cptu-edicao-grupo" data-parte="cortes">
        <legend>Cortes</legend>
        {preFuro !== null && !temCorteDoPreFuro && (
          <button type="button" className="botao secundario"
            onClick={() => mudarEdicao({ ...valendo, cortes: [{ de: 0, ate: preFuro, motivo: 'pré-furo' }, ...valendo.cortes] })}>
            Cortar o pré-furo ({faixaProfundidade(0, preFuro)} m, do cabeçalho)
          </button>
        )}
        {valendo.cortes.map((k, i) => {
          const p = problemasDoCorte(i, k);
          return (
            <div key={i} className="cptu-corte" data-corte={i}>
              <div className="cptu-corte-campos">
                {campoDoCorte(i, 'de', k)}
                {campoDoCorte(i, 'ate', k)}
                <label className="campo">
                  <span className="rotulo">Corte {i + 1}: motivo</span>
                  <input type="text" value={k.motivo} maxLength={200} onChange={(e) => trocarCorte(i, { motivo: e.target.value })} />
                </label>
              </div>
              {p.length > 0 && <Aviso tipo="atencao">{p.join('; ')}.</Aviso>}
              <button type="button" className="ligacao" onClick={() => tirarCorte(i)}>Tirar o corte {i + 1}</button>
            </div>
          );
        })}
        <button type="button" className="botao secundario" onClick={() => {
          const ultimo = valendo.cortes[valendo.cortes.length - 1];
          const de = ultimo ? ultimo.ate : 0;
          // O motivo fica para o engenheiro escrever (pré-furo, trecho perdido…): até lá, a linha o pede e não se grava.
          mudarEdicao({ ...valendo, cortes: [...valendo.cortes, { de, ate: Math.round((de + 0.5) * 100) / 100, motivo: '' }] });
        }}>＋ Corte</button>
        {removidasPorCorte.map((f) => <p key={f} className="cptu-conta-removidas">{f}</p>)}
      </fieldset>

      <fieldset className="cptu-edicao-grupo" data-parte="picos">
        <legend>Picos</legend>
        <label className="cptu-caixa-premissa">
          <input type="checkbox" checked={picosLigado} onChange={(e) => {
            setPicosLigado(e.target.checked);
            mudarPicos(e.target.checked, janelaTxt, limiteTxt, canais);
          }} />
          <span>Remover picos (a leitura que foge da mediana da janela)</span>
        </label>
        {picosLigado && (
          <>
            <div className="cptu-edicao-campos">
              <CampoNumerico rotulo="Janela (leituras, ímpar)" value={janelaTxt} inteiro
                onChange={(e) => { escrever('picos.janela', e.target.value); mudarPicos(true, e.target.value, limiteTxt, canais); }} />
              <CampoNumerico rotulo="Limite (desvios da mediana)" value={limiteTxt}
                onChange={(e) => { escrever('picos.limite', e.target.value); mudarPicos(true, janelaTxt, e.target.value, canais); }} />
            </div>
            <div className="cptu-canais" role="group" aria-label="Os canais em que se procuram picos">
              {(['qc', 'fs', 'u2'] as const).map((k) => (
                <label key={k} className="cptu-caixa-premissa">
                  <input type="checkbox" checked={canais.includes(k)} onChange={(e) => {
                    const novos = e.target.checked ? [...canais, k] : canais.filter((x) => x !== k);
                    setCanais(novos);
                    mudarPicos(true, janelaTxt, limiteTxt, novos);
                  }} />
                  <span>{NOME_DO_CANAL[k]}</span>
                </label>
              ))}
            </div>
            {problemasDosPicos(janelaTxt, limiteTxt, canais).length > 0 && (
              <Aviso tipo="atencao">{problemasDosPicos(janelaTxt, limiteTxt, canais).join('; ')} — até lá, nenhum pico sai.</Aviso>
            )}
          </>
        )}
        {removidasPorPico.map((f) => <p key={f} className="cptu-conta-removidas">{f}</p>)}
        {picosRemovidos.length > 0 && (
          <ul className="cptu-lista-removidas" aria-label="As leituras removidas por pico">
            {picosRemovidos.map((x) => (
              <li key={x.ordem}>{numeroBr(x.z, 2)} m · {x.motivo.replace(/^pico em /, '').replace('u2', 'u₂')}</li>
            ))}
          </ul>
        )}
      </fieldset>

      <fieldset className="cptu-edicao-grupo" data-parte="filtros">
        <legend>Filtros por canal</legend>
        <div className="cptu-edicao-campos">
          {(['qc', 'fs', 'u2'] as const).map((k) => (
            <div key={k}>
              {campoInteiroOuNumero(`filtro.${k}`, `Filtro de ${NOME_DO_CANAL[k]} (leituras)`, valendo.filtros[k], true,
                (n) => mudarEdicao({ ...valendo, filtros: { ...valendo.filtros, [k]: n } }))}
            </div>
          ))}
        </div>
        <p className="cptu-nota">
          Média móvel própria de cada canal (1 = sem filtro): soma-se à média móvel de{' '}
          {plural(mediaMovel, 'leitura', 'leituras')} das premissas, que vale dentro das contas.
        </p>
      </fieldset>

      <fieldset className="cptu-edicao-grupo" data-parte="deslocamento">
        <legend>Deslocamento de profundidade</legend>
        <div className="cptu-edicao-campos">
          {(['fs', 'u2'] as const).map((k) => (
            <div key={k}>
              {campoInteiroOuNumero(`desloc.${k}`, `Deslocamento do ${NOME_DO_CANAL[k]} (cm)`, valendo.deslocamento[k], false,
                (n) => mudarEdicao({ ...valendo, deslocamento: { ...valendo.deslocamento, [k]: n } }))}
            </div>
          ))}
        </div>
        <p className="cptu-nota">Positivo: o canal desce (passa a valer o que valia mais acima); negativo: sobe.</p>
        {sugestaoUsavel ? (
          <p className="cptu-sugestao-deslocamento" data-sugestao-cm={sugestaoUsavel.sugestaoCm}>
            Sugestão: {textoEmCm(sugestaoUsavel.sugestaoCm)} ({plural(Math.abs(sugestaoUsavel.cc.defasagem), 'leitura', 'leituras')},
            r {numeroBr(sugestaoUsavel.cc.r, 2)}){' · '}
            <button type="button" className="ligacao" onClick={() => {
              escrever('desloc.fs', paraCampo(sugestaoUsavel.sugestaoCm, 2));
              mudarEdicao({ ...valendo, deslocamento: { ...valendo.deslocamento, fs: sugestaoUsavel.sugestaoCm } });
            }}>Usar</button>
          </p>
        ) : (
          <p className="cptu-nota">Sem sugestão: a correlação cruzada qc × fs não achou defasagem com pares suficientes.</p>
        )}
        <p className="cptu-nota">
          A correlação cruzada qc × fs (de −{MAXIMO_DA_CORRELACAO} a +{MAXIMO_DA_CORRELACAO} leituras, sobre as leituras com
          os cortes e os picos do rascunho, sem deslocamento nem filtro) está no gráfico{' '}
          <a href="#cptu-correlacao-cruzada">embaixo das trilhas</a>.
        </p>
      </fieldset>

      <fieldset className="cptu-edicao-grupo" data-parte="u0">
        <legend>u₀ não hidrostático</legend>
        <label className="cptu-caixa-premissa">
          <input type="checkbox" checked={u0Modo === 'pontos'}
            onChange={(e) => {
              setTextos((x) => Object.fromEntries(Object.entries(x).filter(([k]) => !k.startsWith('u0.'))));
              // Desligar volta os pontos aos gravados (rodada final da 313): com o gravado hidrostático, o u₀ sai do
              // rascunho inteiro — nada pendente, nenhum «hidrostático → hidrostático», nenhuma exceção gravada.
              if (!e.target.checked) {
                mudarU0('hidrostatico', gravadas.u0Pontos);
                return;
              }
              // Ligar volta aos pontos gravados quando o gravado é por pontos; senão semeia dois pontos que reproduzem
              // o hidrostático — (NA, 0) e (profundidade final, γw·(z − NA)) —: os números ficam como estavam até o
              // engenheiro mexer nos pontos, e o rascunho nunca tem o u₀ NaN.
              const semente = gravadas.u0Modo === 'pontos' && gravadas.u0Pontos.length > 0 ? gravadas.u0Pontos
                : u0Pontos.length > 0 ? u0Pontos
                  : pontosDoHidrostatico(naValendo ?? gravadas.na, v.profundidade_final, gravadas.gamaW);
              mudarU0('pontos', semente);
            }} />
          <span>u₀ por pontos (profundidade × u₀), no lugar do hidrostático</span>
        </label>
        {u0Modo === 'pontos' && (
          <>
            <Aviso tipo="atencao" titulo="▲ Fora das condições homologadas">{grandezasDoU0()}</Aviso>
            <div className="cptu-pontos-u0">
              {u0Pontos.length === 0 && <p className="cptu-nota">Nenhum ponto: com o perfil por pontos, o u₀ precisa deles.</p>}
              {u0Pontos.map((q, i) => (
                <div key={i} className="cptu-ponto-u0">
                  <CampoNumerico rotulo={`Ponto ${i + 1}: z (m)`} value={texto(`u0.${i}.z`, paraCampo(q.z, 3))}
                    onChange={(e) => trocarPonto(i, 'z', e.target.value)} />
                  <CampoNumerico rotulo={`Ponto ${i + 1}: u₀ (kPa)`} value={texto(`u0.${i}.u0`, paraCampo(q.u0, 3))}
                    onChange={(e) => trocarPonto(i, 'u0', e.target.value)} />
                  {u0Pontos.length > 1 && (
                    <button type="button" className="ligacao" onClick={() => {
                      setTextos((x) => Object.fromEntries(Object.entries(x).filter(([k]) => !k.startsWith('u0.'))));
                      mudarU0('pontos', u0Pontos.filter((_, j) => j !== i));
                    }}>Tirar o ponto {i + 1}</button>
                  )}
                </div>
              ))}
              <button type="button" className="botao secundario" onClick={() => {
                const ultimo = u0Pontos[u0Pontos.length - 1];
                // Um metro abaixo do último, arredondado ao milímetro (0,86 + 1 não pode virar 1,8599999…).
                mudarU0('pontos', [...u0Pontos, { z: ultimo ? Math.round((ultimo.z + 1) * 1000) / 1000 : 0, u0: ultimo ? ultimo.u0 : 0 }]);
              }}>＋ Ponto</button>
              <p className="cptu-nota">
                Ligado, o perfil começa com dois pontos que reproduzem o hidrostático — o NA com 0 kPa e a profundidade
                final com γw·(z − NA) —: os números só mudam quando você muda os pontos.
              </p>
              {u0Pontos.length === 1 && (
                <p className="cptu-nota">
                  Com um ponto só, o u₀ é o dele em toda a vertical (fora dos pontos, vale o mais perto): acrescente os outros.
                  O perfil por pontos fica com pelo menos um; para voltar ao hidrostático, desmarque a caixa.
                </p>
              )}
            </div>
          </>
        )}
      </fieldset>

      <NaDasDissipacoes c={c} v={v} naValendo={naValendo} aoUsar={usarNa} />

      {outrasPremissas.length > 0 && (
        <p className="cptu-nota">
          O rascunho também tem premissas do «Simular» ({outrasPremissas.join(', ')}): elas ficam no rascunho depois de
          gravar a edição.
        </p>
      )}
      <div className="cptu-edicao-acoes">
        {sujo && (
          <button type="button" className="botao secundario" onClick={() => {
            setTextos({});
            setPicosLigado(gravada.picos !== null);
            setCanais(gravada.picos?.canais ?? []);
            aoMudar(rascunhoSemAEdicao(rascunho));
            // O botão some: o foco vai ao «Gravar a edição» que fica (rodada do agent-browser da 313).
            focarOPrimeiro('section.cptu-edicao .cptu-edicao-acoes > button');
          }}>Sair sem gravar a edição</button>
        )}
        {bloqueio ? acaoBloqueada('Gravar a edição') : (
          <button type="button" className="botao" aria-disabled={motivoDoGravar ? true : undefined}
            aria-describedby={motivoDoGravar ? idDoGravar : undefined}
            onClick={() => { if (!motivoDoGravar) setJanela(true); }}>
            Gravar a edição
          </button>
        )}
        {motivoDoGravar && !bloqueio && <small id={idDoGravar} className="cptu-motivo-bloqueio">{motivoDoGravar}</small>}
      </div>
      {janela && rascunho && (
        <JanelaDaEdicao v={v} gravada={gravada} gravadas={gravadas} rascunho={rascunho} correlacao={correlacao}
          aoFechar={() => setJanela(false)}
          aoPronto={async () => { setJanela(false); setTextos({}); await aoGravado(rascunhoSemAEdicao(rascunho)); }} />
      )}
    </section>
  );
}

/**
 * «N grandezas saem do PDF e do Portal» — o u₀ por pontos fora das condições homologadas; N é a conta única do visual
 * (`colunasQueSaemDaHomologacao`: as homologadas que saem — 33 no motor 1.0.0).
 */
function grandezasDoU0(): string {
  return `${plural(COM_U0_POR_PONTOS.length, 'grandeza sai', 'grandezas saem')} do PDF e do Portal`;
}

/** O NA sugerido pelas dissipações da vertical (como na dissipação aberta): sugestão, que vai ao rascunho. */
function NaDasDissipacoes({ c, v, naValendo, aoUsar }: {
  c: CampanhaCompleta; v: VerticalCompleta; naValendo: number | null; aoUsar(na: number): void;
}) {
  // Só as dissipações desta vertical: as curvas e o resultado gravado das outras não são lidos (R9).
  const { itens, erro } = useDissipacoesNaTela(c, v.id);
  const daVertical = itens ?? [];
  let corpo: ReactNode;
  if (erro) corpo = <ErroDaApi erro={erro} />;
  else if (!itens) corpo = <p className="cptu-nota">Lendo as dissipações da vertical…</p>;
  else if (daVertical.length === 0) corpo = <p className="cptu-nota">A vertical {v.codigo} não tem dissipação.</p>;
  else {
    corpo = (
      <ul className="cptu-na-das-dissipacoes">
        {daVertical.map((x) => {
          const s = interpretar(x, escolhasGravadas(x.d)).s;
          return (
            <li key={x.d.id}>
              <b>{numeroBr(x.d.profundidade, 2)} m</b>:{' '}
              {s.naEstimado === null ? 'sem sugestão — a curva não parou' : (
                <>
                  NA sugerido {numeroBr(s.naEstimado, 2)} m{' '}
                  <button type="button" className="ligacao" onClick={() => aoUsar(s.naEstimado as number)}>Usar no rascunho</button>
                </>
              )}
            </li>
          );
        })}
      </ul>
    );
  }
  return (
    <fieldset className="cptu-edicao-grupo" data-parte="na">
      <legend>NA pelas dissipações</legend>
      <p className="cptu-nota">
        NA da vertical: {v.nivel_agua === null ? 'não informado' : `${numeroBr(v.nivel_agua, 2)} m`} (o cabeçalho)
        {naValendo !== v.nivel_agua && naValendo !== null ? ` · no rascunho: ${numeroBr(naValendo, 2)} m` : ''}. Quando a
        curva para, NA = z − u∞/γw — uma sugestão, nunca sozinha.
      </p>
      {corpo}
    </fieldset>
  );
}

/**
 * O motivo e o que vai (R10): a edição inteira (a partir da vazia copiada; sem ajuste nenhum, `null`), as premissas
 * da vertical inteiras a partir do gravado quando o u₀ mudou, e o NA pelo cabeçalho.
 */
function JanelaDaEdicao({ v, gravada, gravadas, rascunho, correlacao, aoFechar, aoPronto }: {
  v: VerticalCompleta;
  gravada: Edicao;
  gravadas: Premissas;
  rascunho: Rascunho;
  correlacao: CorrelacaoDaEdicao | null;
  aoFechar(): void;
  aoPronto(): Promise<void>;
}) {
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  const nova = rascunho.edicao !== undefined ? edicaoInteira(rascunho.edicao) : null;
  const u0Modo = rascunho.premissas?.u0Modo ?? gravadas.u0Modo;
  const u0Pontos = rascunho.premissas?.u0Pontos ?? gravadas.u0Pontos;
  const mudouU0 = rascunho.premissas?.u0Modo !== undefined || rascunho.premissas?.u0Pontos !== undefined;
  const linhas: string[] = [];
  if (nova) {
    const a = partesDaEdicao(gravada);
    const d = partesDaEdicao(nova);
    const rotulos = { cortes: 'Cortes', picos: 'Picos', filtros: 'Filtros por canal', deslocamento: 'Deslocamento' } as const;
    for (const k of Object.keys(rotulos) as (keyof typeof rotulos)[]) {
      if (a[k] !== d[k]) linhas.push(`${rotulos[k]}: ${a[k]} → ${d[k]}`);
    }
    if (correlacao) linhas.push(...frasesDasRemovidas(correlacao.removidas, false), ...frasesDasRemovidas(correlacao.removidas, true));
  }
  if (mudouU0) linhas.push(`u₀: ${textoDoU0(gravadas.u0Modo, gravadas.u0Pontos)} → ${textoDoU0(u0Modo, u0Pontos)}`);
  if (rascunho.nivelAgua !== undefined) {
    linhas.push(`NA: ${v.nivel_agua === null ? 'não informado' : `${numeroBr(v.nivel_agua, 2)} m`} → ${
      rascunho.nivelAgua === null ? 'não informado' : `${numeroBr(rascunho.nivelAgua, 2)} m`}`);
  }
  const falta = motivo.trim().length < 10 ? 'Diga o motivo (pelo menos 10 caracteres): ele vai para a trilha.' : null;

  const gravar = async () => {
    if (falta || ocupado) return;
    setOcupado(true);
    setErro(null);
    try {
      const corpo: AlteracaoDaVertical = { motivo: motivo.trim() };
      if (nova) corpo.edicao = mesmaEdicao(nova, null) ? null : nova;
      if (mudouU0) corpo.premissas = premissasParaAVertical(v, { u0Modo, u0Pontos });
      if (rascunho.nivelAgua !== undefined) corpo.nivelAgua = rascunho.nivelAgua;
      await alterarVertical(v.id, corpo);
      await aoPronto();
    } catch (e) {
      setErro(e);
      setOcupado(false);
    }
  };
  return (
    <Dialogo aberto titulo="Gravar a edição" rotulo={`Vertical ${v.codigo}`} aoFechar={aoFechar}>
      <FormularioDaJanela aoEnviar={() => { void gravar(); }}>
        <ErroDaApi erro={erro} />
        <p className="sub">
          Vai para a vertical {v.codigo}. As leituras do equipamento não mudam (RN-253): a edição se aplica antes das
          contas, e a trilha guarda quem gravou, quando e o motivo.
        </p>
        <ul className="cptu-lista-da-janela">
          {linhas.map((l) => <li key={l}>{l}</li>)}
        </ul>
        {mudouU0 && u0Modo === 'pontos' && (
          <Aviso tipo="atencao" titulo="▲ u₀ por pontos: fora das condições homologadas">{grandezasDoU0()}.</Aviso>
        )}
        <Area rotulo="Motivo" rows={2} value={motivo} maxLength={2000}
          dica="Vai para a trilha, com quem gravou e quando." onChange={(e) => setMotivo(e.target.value)} />
        {falta && <p className="cptu-falta" role="status">{falta}</p>}
        <div className="rodape-dialogo">
          <button type="submit" className="botao" aria-disabled={!!falta || ocupado}>
            {ocupado ? 'Gravando…' : 'Gravar'}
          </button>
          <button type="button" className="botao secundario" onClick={aoFechar}>Cancelar</button>
        </div>
      </FormularioDaJanela>
    </Dialogo>
  );
}
